import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getPagosComisiones,
} from "../helpers/rutaComisiones";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FORMATOS
   ========================================================= */

const formatDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value._id || value.id || "";
};

const nombrePersona = (persona) => {
  if (!persona) return "-";

  if (typeof persona === "string") {
    return persona;
  }

  return (
    [
      persona.nombres,
      persona.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    persona.email ||
    "-"
  );
};

const textoEstado = (estado) => {
  const value = String(
    estado || ""
  ).toUpperCase();

  if (value === "ACTIVO") {
    return "Activo";
  }

  if (value === "ANULADO") {
    return "Anulado";
  }

  return value || "-";
};

const colorEstado = (estado) => {
  const value = String(
    estado || ""
  ).toUpperCase();

  if (value === "ACTIVO") {
    return "success";
  }

  if (value === "ANULADO") {
    return "danger";
  }

  return "secondary";
};

const tipoLiquidacion = (item) => {
  const tipo =
    String(
      item?.liquidacion?.tipo || ""
    ).toUpperCase();

  if (tipo === "MENSUAL") {
    return "Mensual";
  }

  if (tipo === "SEMANAL") {
    return "Semanal";
  }

  return "-";
};

const periodoLiquidacion = (item) => {
  const desde =
    item?.liquidacion?.periodoDesde;

  const hasta =
    item?.liquidacion?.periodoHasta;

  if (!desde && !hasta) {
    return "-";
  }

  return `${formatDate(
    desde
  )} → ${formatDate(hasta)}`;
};

/* =========================================================
   COMPONENTE
   ========================================================= */

export default function ReporteComisionesPagos() {
  const [desde, setDesde] =
    useState("");

  const [hasta, setHasta] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [vendedor, setVendedor] =
    useState("");

  const [estado, setEstado] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [pagos, setPagos] =
    useState([]);

  const [
    vendedoresBase,
    setVendedoresBase,
  ] = useState([]);

  const [resumen, setResumen] =
    useState({
      cantidadPagos: 0,
      totalPagadoUSD: 0,
    });

  /* =======================================================
     VENDEDORES
     ======================================================= */

  const vendedoresDisponibles =
    useMemo(() => {
      const map = new Map();

      vendedoresBase.forEach(
        (item) => {
          const id =
            getId(item?.vendedor);

          const nombre =
            nombrePersona(
              item?.vendedor
            );

          if (
            id &&
            nombre &&
            !map.has(id)
          ) {
            map.set(id, nombre);
          }
        }
      );

      return Array.from(
        map.entries()
      )
        .map(([id, nombre]) => ({
          id,
          nombre,
        }))
        .sort((a, b) =>
          a.nombre.localeCompare(
            b.nombre,
            "es"
          )
        );
    }, [vendedoresBase]);

  /* =======================================================
     CARGAR REPORTE
     ======================================================= */

  const cargarReporte = async (
    cargaInicial = false
  ) => {
    setLoading(true);
    setError("");

    try {
      const data =
        await getPagosComisiones({
          desde,
          hasta,
          sede,
          vendedor,
          estado,
        });

      const registros =
        Array.isArray(
          data?.pagos
        )
          ? data.pagos
          : [];

      setPagos(registros);

      setResumen({
        cantidadPagos:
          Number(
            data?.resumen
              ?.cantidadPagos ??
              registros.length
          ),

        totalPagadoUSD:
          Number(
            data?.resumen
              ?.totalPagadoUSD ||
              0
          ),
      });

      if (cargaInicial) {
        setVendedoresBase(
          registros
        );
      }
    } catch (err) {
      console.error(
        "Error informe pagos comisiones:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo cargar el informe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros = () => {
    setDesde("");
    setHasta("");
    setSede("");
    setVendedor("");
    setEstado("");
  };

  /* =======================================================
     CSV
     ======================================================= */

  const generarCSV = () => {
    if (pagos.length === 0) {
      return;
    }

    const encabezados = [
      "Fecha",
      "Vendedor",
      "Sede",
      "Tipo",
      "Periodo",
      "Monto USD",
      "Referencia",
      "Registrado por",
      "Estado",
    ];

    const escapar = (value) => {
      const texto =
        String(value ?? "");

      return `"${texto.replace(
        /"/g,
        '""'
      )}"`;
    };

    const filas =
      pagos.map((item) => [
        formatDate(
          item?.fechaPago
        ),

        nombrePersona(
          item?.vendedor
        ),

        item?.sede || "",

        tipoLiquidacion(
          item
        ),

        periodoLiquidacion(
          item
        ),

        Number(
          item?.montoUSD || 0
        ),

        item?.referencia || "",

        nombrePersona(
          item?.registradoPor
        ),

        textoEstado(
          item?.estado
        ),
      ]);

    const contenido = [
      encabezados,
      ...filas,
    ]
      .map((fila) =>
        fila
          .map(escapar)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(
      [
        "\uFEFF",
        contenido,
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      "pagos-comisiones-riders.csv";

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     PDF
     ======================================================= */

  const generarPDF = async () => {
    try {
      const doc =
        new jsPDF(
          "landscape",
          "mm",
          "a4"
        );

      const logoImg =
        await cargarImagen(logo);

      doc.addImage(
        logoImg,
        "PNG",
        10,
        8,
        34,
        12
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "Informe de Pagos de Comisiones",
        50,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        "Riders Miami",
        50,
        20
      );

      doc.text(
        `Desde: ${
          desde || "Todas"
        }   Hasta: ${
          hasta || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      const vendedorNombre =
        vendedoresDisponibles.find(
          (item) =>
            item.id === vendedor
        )?.nombre || "Todos";

      doc.text(
        `Sede: ${
          sede || "Todas"
        } | Vendedor: ${vendedorNombre} | Estado: ${
          estado
            ? textoEstado(
                estado
              )
            : "Todos"
        }`,
        287,
        20,
        {
          align: "right",
        }
      );

      doc.setDrawColor(180);

      doc.line(
        10,
        24,
        287,
        24
      );

      /* RESUMEN */

      const resumenY = 28;
      const boxW = 80;
      const boxH = 14;
      const gap = 4;

      const itemsResumen = [
        {
          label: "Pagos",
          value:
            String(
              resumen
                .cantidadPagos ||
                0
            ),
        },
        {
          label:
            "Total pagado",
          value:
            formatUSDPdf(
              resumen
                .totalPagadoUSD
            ),
        },
      ];

      itemsResumen.forEach(
        (item, i) => {
          const x =
            10 +
            i *
              (boxW + gap);

          doc.setDrawColor(210);

          doc.roundedRect(
            x,
            resumenY,
            boxW,
            boxH,
            1.5,
            1.5
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          doc.text(
            item.label,
            x + 2,
            resumenY + 4.5
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            String(item.value),
            x + 2,
            resumenY + 10
          );
        }
      );

      /* TABLA */

      const body =
        pagos.map(
          (item) => [
            formatDate(
              item?.fechaPago
            ),

            nombrePersona(
              item?.vendedor
            ),

            item?.sede || "",

            tipoLiquidacion(
              item
            ),

            periodoLiquidacion(
              item
            ),

            formatUSDPdf(
              item?.montoUSD
            ),

            item?.referencia ||
              "-",

            nombrePersona(
              item?.registradoPor
            ),

            textoEstado(
              item?.estado
            ),
          ]
        );

      autoTable(doc, {
        startY: 48,

        theme: "grid",

        margin: {
          left: 8,
          right: 8,
        },

        head: [
          [
            "Fecha",
            "Vendedor",
            "Sede",
            "Tipo",
            "Período",
            "Monto",
            "Referencia",
            "Registrado por",
            "Estado",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            "",
            "",
            "",
            formatUSDPdf(
              resumen
                .totalPagadoUSD
            ),
            "",
            "",
            "",
          ],
        ],

        styles: {
          fontSize: 7,
          cellPadding: 1,
          valign: "middle",
        },

        headStyles: {
          fillColor: [
            33,
            37,
            41,
          ],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        footStyles: {
          fillColor: [
            245,
            245,
            245,
          ],
          textColor: 20,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 23,
          },

          1: {
            cellWidth: 36,
          },

          2: {
            cellWidth: 23,
          },

          3: {
            cellWidth: 22,
          },

          4: {
            cellWidth: 46,
          },

          5: {
            cellWidth: 28,
            halign: "right",
          },

          6: {
            cellWidth: 30,
          },

          7: {
            cellWidth: 36,
          },

          8: {
            cellWidth: 24,
          },
        },

        didDrawPage: () => {
          const pageCount =
            doc.internal
              .getNumberOfPages();

          doc.setFontSize(8);

          doc.setTextColor(100);

          doc.text(
            `Página ${pageCount}`,
            doc.internal.pageSize
              .getWidth() - 10,
            doc.internal.pageSize
              .getHeight() - 6,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        "pagos-comisiones-riders.pdf"
      );
    } catch (error) {
      console.error(
        "Error generando PDF:",
        error
      );

      alert(
        "No se pudo generar el PDF."
      );
    }
  };

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">

            <div>
              <h4 className="mb-0">
                Informe de Pagos
                de Comisiones
              </h4>

              <small className="text-muted">
                Riders Miami
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={() =>
                  cargarReporte(false)
                }
                disabled={loading}
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={generarCSV}
                disabled={
                  loading ||
                  pagos.length === 0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={
                  loading ||
                  pagos.length === 0
                }
              >
                PDF
              </Button>

            </div>
          </div>

          {/* FILTROS */}

          <Row className="g-2 mb-3 align-items-end">

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={sede}
                onChange={(e) =>
                  setSede(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Vendedor
              </Form.Label>

              <Form.Select
                value={vendedor}
                onChange={(e) =>
                  setVendedor(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                {vendedoresDisponibles.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.nombre}
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Estado
              </Form.Label>

              <Form.Select
                value={estado}
                onChange={(e) =>
                  setEstado(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                <option value="ACTIVO">
                  Activo
                </option>

                <option value="ANULADO">
                  Anulado
                </option>
              </Form.Select>
            </Col>

          </Row>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* TOTALES */}

          <Row className="g-2 mb-3">

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Cantidad de pagos
                  </small>

                  <h5 className="mb-0">
                    {resumen
                      .cantidadPagos ||
                      0}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">

                  <small className="text-muted">
                    Total pagado
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      resumen
                        .totalPagadoUSD
                    )}
                  </h5>

                </Card.Body>
              </Card>
            </Col>

          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "65vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >

              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th>Fecha</th>

                  <th>Vendedor</th>

                  <th>Sede</th>

                  <th>Tipo</th>

                  <th>Período</th>

                  <th className="text-end">
                    Monto
                  </th>

                  <th>
                    Referencia
                  </th>

                  <th>
                    Registrado por
                  </th>

                  <th>
                    Estado
                  </th>
                </tr>
              </thead>

              <tbody>

                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />

                      Cargando informe...
                    </td>
                  </tr>
                ) : pagos.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="text-center py-4 text-muted"
                    >
                      No hay pagos para
                      los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  pagos.map(
                    (item) => (
                      <tr
                        key={item._id}
                      >
                        <td>
                          {formatDate(
                            item
                              .fechaPago
                          )}
                        </td>

                        <td className="fw-semibold">
                          {nombrePersona(
                            item
                              .vendedor
                          )}
                        </td>

                        <td>
                          {item.sede}
                        </td>

                        <td>
                          {item
                            ?.liquidacion
                            ?.tipo ? (
                            <Badge
                              bg={
                                item
                                  .liquidacion
                                  .tipo ===
                                "MENSUAL"
                                  ? "info"
                                  : "secondary"
                              }
                            >
                              {tipoLiquidacion(
                                item
                              )}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {periodoLiquidacion(
                            item
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .montoUSD
                          )}
                        </td>

                        <td>
                          {item
                            .referencia ||
                            "-"}
                        </td>

                        <td>
                          {nombrePersona(
                            item
                              .registradoPor
                          )}
                        </td>

                        <td>
                          <Badge
                            bg={colorEstado(
                              item.estado
                            )}
                          >
                            {textoEstado(
                              item.estado
                            )}
                          </Badge>
                        </td>
                      </tr>
                    )
                  )
                )}

              </tbody>

              {pagos.length > 0 && (
                <tfoot>
                  <tr>

                    <th colSpan="5">
                      Totales
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        resumen
                          .totalPagadoUSD
                      )}
                    </th>

                    <th colSpan="3" />

                  </tr>
                </tfoot>
              )}

            </Table>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
}